// WO 3 — multi-slot upload tray. Renders 3 tiles (Photo, PAN, Aadhaar)
// for a single stakeholder so the founder can upload all required
// documents in one go instead of being prompted slot-by-slot. The
// Aadhaar tile accepts multiple files (front + back) or a single PDF.
//
// Client-side pre-flight (WO 8): type whitelist + 10 MB cap per file
// — surfaces a friendly inline error and refuses the file before any
// network call.
//
// The widget itself is the question. The accompanying Infi message is
// kept short by the WO 3 system prompt rule.

import { useRef, useState } from "react";
import { Upload, Camera, CheckCircle2, AlertTriangle, FileText, Loader2 } from "lucide-react";
import { maybeDownscaleAll } from "../lib/imageDownscale";

const MAX_BYTES = 10 * 1024 * 1024;
const TYPE_LABEL = {
  photo:         "Photograph",
  pan_proof:     "PAN card",
  aadhaar_proof: "Aadhaar card",
};

function bytesLabel(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function clientValidate(file, accept) {
  if (!file) return "No file selected";
  if (file.size > MAX_BYTES) {
    return `${file.name} is ${bytesLabel(file.size)} — limit is 10 MB.`;
  }
  // accept is a comma-separated list of MIME types; allow `image/*`.
  const allowed = accept.split(",").map((s) => s.trim());
  const mime = (file.type || "").toLowerCase();
  const ok = allowed.some((a) => {
    if (a.endsWith("/*")) return mime.startsWith(a.slice(0, a.length - 1));
    return a === mime;
  });
  if (!ok && mime) {
    return `${file.name} is ${mime} — please upload JPG, PNG, WEBP, or PDF.`;
  }
  return null;
}

function Tile({ slot, label, accept, multi, files, uploaded, busy, onPick, error }) {
  const ref = useRef(null);
  const camRef = useRef(null);
  const done = uploaded || (files && files.length > 0);

  return (
    <div
      data-testid={`tray-tile-${slot}`}
      className={[
        "border rounded-xl p-4 transition-colors bg-white",
        done ? "border-emerald-300 bg-emerald-50/40" :
               error ? "border-red-300 bg-red-50/30" :
                        "border-slate-300 hover:border-[#C5283F]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
          {label}{multi && !done ? " (front + back)" : ""}
        </div>
        {done ? (
          <CheckCircle2 size={16} className="text-emerald-600" />
        ) : busy ? (
          <Loader2 size={16} className="text-slate-500 animate-spin" />
        ) : null}
      </div>

      {done ? (
        <div className="space-y-1">
          {(files || []).map((f, i) => (
            <div key={`${f.name || "file"}-${f.size || 0}-${i}`} className="flex items-center gap-2 text-sm text-slate-700">
              <FileText size={14} className="text-emerald-600 shrink-0" />
              <span className="truncate" title={f.name}>{f.name}</span>
              <span className="text-xs text-slate-500 shrink-0">{bytesLabel(f.size)}</span>
            </div>
          ))}
          {uploaded && (!files || files.length === 0) && (
            <div className="text-sm text-emerald-700">Uploaded ✓</div>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            data-testid={`tray-tile-${slot}-pick-btn`}
            className="w-full border border-dashed border-slate-300 rounded-lg py-4 text-center text-sm text-slate-700 hover:border-[#C5283F] hover:bg-slate-50 disabled:opacity-50"
          >
            <Upload size={16} className="mx-auto mb-1 text-slate-500" />
            {multi ? "Click to add front + back" : "Click to upload"}
            <div className="text-[11px] text-slate-500 mt-1">JPG, PNG, or PDF · up to 10 MB</div>
          </button>
          <button
            type="button"
            onClick={() => camRef.current?.click()}
            disabled={busy}
            data-testid={`tray-tile-${slot}-camera-btn`}
            className="mt-2 w-full text-xs text-slate-600 inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-slate-200 sm:hidden disabled:opacity-50"
          >
            <Camera size={12} /> Take photo
          </button>
        </>
      )}
      {error && (
        <div className="mt-2 text-[12px] text-red-700 flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={!!multi}
        className="hidden"
        data-testid={`tray-tile-${slot}-input`}
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          if (!picked.length) return;
          onPick(picked);
          e.target.value = "";
        }}
      />
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          if (!picked.length) return;
          onPick(picked);
          e.target.value = "";
        }}
      />
    </div>
  );
}

export default function MultiSlotUploadWidget({
  subjectRef, slots, busy, onSubmit,
}) {
  // slots = [{slot, target, label, accept, multi, uploaded}, ...]
  // Local state per slot: pending File[] + error string.
  const [picks, setPicks] = useState(
    () => Object.fromEntries((slots || []).map((s) => [s.slot, []]))
  );
  const [errors, setErrors] = useState({});

  async function handlePick(slot, files) {
    const slotMeta = slots.find((s) => s.slot === slot);
    // Validate each file
    const errs = [];
    for (const f of files) {
      const e = clientValidate(f, slotMeta.accept);
      if (e) errs.push(e);
    }
    if (errs.length) {
      setErrors((prev) => ({ ...prev, [slot]: errs.join(" ") }));
      return;
    }
    // WO 8.2 — downscale large phone images BEFORE the network call.
    // Returns originals untouched for PDFs / HEIC / small files.
    let prepared;
    try {
      prepared = await maybeDownscaleAll(files);
    } catch {
      prepared = files;
    }
    // For non-multi slots, replace; for multi (aadhaar), append up to 2
    const limit = slotMeta.multi ? 2 : 1;
    const next = (slotMeta.multi ? [...(picks[slot] || []), ...prepared] : prepared).slice(0, limit);
    setPicks((prev) => ({ ...prev, [slot]: next }));
    setErrors((prev) => ({ ...prev, [slot]: null }));
  }

  function readyToSubmit() {
    return (slots || []).every((s) => s.uploaded || (picks[s.slot] || []).length > 0);
  }

  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="multi-slot-upload-widget">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
        Documents for {subjectRef}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(slots || []).map((s) => (
          <Tile
            key={s.slot}
            slot={s.slot}
            label={s.label || TYPE_LABEL[s.slot] || s.slot}
            accept={s.accept}
            multi={s.multi}
            uploaded={s.uploaded}
            files={picks[s.slot]}
            busy={busy}
            error={errors[s.slot]}
            onPick={(files) => handlePick(s.slot, files)}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => onSubmit(picks)}
        disabled={busy || !readyToSubmit()}
        data-testid="multi-slot-submit-btn"
        className="mt-4 w-full bg-[#C5283F] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#A82035] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Uploading…
          </>
        ) : (
          <>Continue with these documents</>
        )}
      </button>
      <div className="mt-2 text-[11px] text-slate-500 text-center">
        Aadhaar — upload front and back (or a single multi-page PDF).
      </div>
    </div>
  );
}
